/**
 * VIN Decoding Service for NAZ AI
 * Primary Layer: NHTSA API
 * Fallback Layer: Gemini / Local Pattern Matching
 */

import { validateVINChecksum } from '../logic/identityEngine';

export interface VinData {
    make: string;
    model: string;
    year: string;
    engine?: string;
    fuel?: string;
    transmission?: string;
    vin_checksum_valid?: boolean;
}

export const vinService = {
    async decode(vin: string): Promise<VinData | null> {
        if (!vin || vin.length < 11) return null;

        // Validate checksum first
        const checksumValid = validateVINChecksum(vin);

        try {
            // Use the Worker Proxy to avoid CORS issues
            const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || "http://localhost:8787";
            const response = await fetch(`${API_BASE_URL}/diagnose`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ action: "decode_vin", vin })
            });

            if (!response.ok) throw new Error("VIN Proxy failed");

            const wrapper = await response.json() as any;
            if (wrapper.status !== 'success' || !wrapper.data) return null;

            const data = wrapper.data;
            const result = data.Results?.[0]; // NHTSA Structure

            if (result && result.Make) {
                return {
                    make: this.mapMakeToArabic(result.Make),
                    model: result.Model || "",
                    year: result.ModelYear || "",
                    engine: result.DisplacementL ? `${result.DisplacementL}L` : result.EngineModel || undefined,
                    fuel: result.FuelTypePrimary || undefined,
                    transmission: result.TransmissionStyle || undefined,
                    vin_checksum_valid: checksumValid
                };
            }
        } catch (error) {
            console.error("VIN Decoding Error:", error);
        }

        return null;
    },

    mapMakeToArabic(make: string): string {
        const map: Record<string, string> = {
            "TOYOTA": "تويوتا",
            "HYUNDAI": "هيونداي",
            "NISSAN": "نيسان",
            "MITSUBISHI": "ميتسوبيشي",
            "KIA": "كيا",
            "ISUZU": "إيسوزو",
            "MAZDA": "مازدا",
            "FORD": "فورد",
            "CHEVROLET": "شيفروليه",
            "MERCEDES-BENZ": "مرسيدس",
            "BMW": "بي ام دبليو"
        };
        return map[make.toUpperCase()] || make;
    }
};
